import { useEffect } from 'react';
import { useLanguage } from './i18n';

const SITE_URL = 'https://academy.ofalycee.org';
const SUFFIX = ' | OFA Academy™';

const PAGE_META = {
  home: {
    en: ['OFA Academy™ | Online Bilingual Learning by OFA Lycée', 'OFA Academy™ — Online bilingual learning by OFA Lycée™. Live, hybrid and on-demand courses in French, humanities, AI and summer programs.'],
    fr: ['OFA Academy™ | Apprentissage bilingue en ligne', "OFA Academy™ — l'académie bilingue en ligne d'OFA Lycée™. Cours en direct, hybrides ou à la demande : français, humanités, IA et Summer School."],
  },
  vision: {
    en: ['Our Vision – A Global Bilingual Academy', 'Discover the vision of OFA Academy™: an independent online academy combining French rigor and American flexibility for learners worldwide.'],
    fr: ['Notre vision – Une académie bilingue mondiale', "Découvrez la vision d'OFA Academy™ : une académie en ligne indépendante alliant rigueur française et flexibilité américaine, partout dans le monde."],
  },
  programs: {
    en: ['Online Courses & Programs', 'Browse OFA Academy™ online courses: French (DELF, DALF), history, philosophy, AI, college counseling and teacher training. Live, hybrid or on-demand.'],
    fr: ['Cours et programmes en ligne', "Découvrez les cours en ligne d'OFA Academy™ : français (DELF, DALF), histoire, philosophie, IA, orientation universitaire et formation des enseignants."],
  },
  summer: {
    en: ['Summer Program 2027 – Coming Soon', 'OFA Academy™ Summer Program 2027 (July 1 – August 31): an online French program from kindergarten to middle school. Coming soon.'],
    fr: ['Summer Program 2027 – Bientôt disponible', "Summer Program 2027 d'OFA Academy™ (1er juillet – 31 août) : un programme en ligne en français, de la Grande Section à la 3ème. Bientôt disponible."],
  },
  certificates: {
    en: ['Certificates & Credentials', 'Earn verifiable OFA Academy™ certificates in languages, academic skills, AI literacy and professional development.'],
    fr: ['Certificats et attestations', 'Obtenez des certificats OFA Academy™ vérifiables en langues, compétences académiques, IA et développement professionnel.'],
  },
  team: {
    en: ['Our Team – Founders & Educators', 'Meet the founders and educators of OFA Academy™, experienced teachers and school leaders in French, American and international education.'],
    fr: ['Notre équipe – Fondatrices et enseignants', "Rencontrez l'équipe d'OFA Academy™ : enseignants et chefs d'établissement expérimentés des systèmes français, américain et international."],
  },
  contribute: {
    en: ['Teach or Contribute', 'Teachers, researchers and experts: propose a course or workshop and join the international community of OFA Academy™ instructors.'],
    fr: ['Enseigner ou contribuer', "Enseignants, chercheurs, experts : proposez un cours ou un atelier et rejoignez la communauté internationale des intervenants d'OFA Academy™."],
  },
  perspectives: {
    en: ['Perspectives – Articles on Education', 'Articles and insights from OFA Academy™ on bilingual education, AI in schools, international families and the future of learning.'],
    fr: ["Perspectives – Articles sur l'éducation", "Articles et réflexions d'OFA Academy™ sur l'éducation bilingue, l'IA à l'école, les familles internationales et l'avenir de l'apprentissage."],
  },
  privacy: {
    en: ['Privacy Policy', 'How OFA Academy™ collects, uses and protects your personal data (GDPR, CCPA).'],
    fr: ['Politique de confidentialité', 'Comment OFA Academy™ collecte, utilise et protège vos données personnelles (RGPD, CCPA).'],
  },
  terms: {
    en: ['Terms of Service', 'Terms and conditions for using OFA Academy™ online courses, enrollment and payments.'],
    fr: ["Conditions d'utilisation", "Conditions d'utilisation des cours en ligne OFA Academy™ : inscription et paiements."],
  },
};

const setMeta = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el && value) el.setAttribute(attr, value);
};

const clip = (text, max) => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trim()}…` : clean;
};

const setAlternate = (hreflang, href) => {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
};

export function usePageMeta(title, description) {
  const { lang } = useLanguage();
  useEffect(() => {
    if (!title) return;
    const fullTitle = title.includes('OFA Academy') ? title : `${title}${SUFFIX}`;
    const desc = clip(description, 160);
    const enUrl = `${SITE_URL}${window.location.pathname}`;
    const frUrl = `${enUrl}?lang=fr`;
    const url = lang === 'fr' ? frUrl : enUrl;
    document.title = fullTitle;
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:url"]', 'content', url);
    setAlternate('en', enUrl);
    setAlternate('fr', frUrl);
    setAlternate('x-default', enUrl);
  }, [title, description, lang]);
}

export function useStaticPageMeta(key) {
  const { lang } = useLanguage();
  const [title, description] = PAGE_META[key][lang] || PAGE_META[key].en;
  usePageMeta(title, description);
}
